import {
  ELEMENTS,
  allToken,
  elementLabel,
  isElement,
  personToken,
  type ElementId,
  type TargetPayload,
} from '../shared/domain';
import type { Person } from './api';
import { el } from './ui';

export function buildTargets(people: Person[], tokens: string[]): TargetPayload {
  const labels: string[] = [];
  const snapshot: string[] = [];
  for (const token of tokens) {
    if (token.startsWith('all:')) {
      const element = token.slice(4);
      labels.push(`All ${elementLabel(element)}`);
      for (const person of people) {
        if (person.active && person.element === element) snapshot.push(person.call_sign);
      }
    } else if (token.startsWith('person:')) {
      const person = people.find((p) => p.id === token.slice(7));
      if (person) {
        labels.push(person.call_sign);
        snapshot.push(person.call_sign);
      }
    }
  }
  return {
    tokens,
    labels,
    snapshot: [...new Set(snapshot)],
  };
}

export function peopleField(initialPeople: Person[]): {
  element: HTMLElement;
  getElement: () => string;
  getElements: () => string[];
  getTokens: () => string[];
  set: (element: string | string[], tokens: string[]) => void;
  setPeople: (people: Person[]) => void;
} {
  let people = initialPeople.filter((p) => p.active);
  let elementIds: ElementId[] = [];
  let tokens: string[] = [];

  const elementRow = el('div', { className: 'chips' });
  const peopleRow = el('div', { className: 'stack' });
  const summary = el('p', { className: 'summary' });
  const hint = el('p', {
    className: 'field-hint',
    textContent: 'Pick one or more elements to see call signs.',
  });

  function paintSummary(): void {
    const built = buildTargets(people, tokens);
    summary.textContent = built.labels.join(' · ');
  }

  function paintElements(): void {
    elementRow.replaceChildren();
    for (const element of ELEMENTS) {
      const on = elementIds.includes(element.id);
      const btn = el('button', {
        type: 'button',
        className: on ? 'chip on' : 'chip',
        textContent: element.label,
        'aria-pressed': on ? 'true' : 'false',
      });
      btn.addEventListener('click', () => {
        elementIds = on
          ? elementIds.filter((id) => id !== element.id)
          : [...elementIds, element.id];
        paintElements();
        paintPeople();
      });
      elementRow.append(btn);
    }
  }

  function chip(label: string, token: string): HTMLButtonElement {
    const on = tokens.includes(token);
    const btn = el('button', {
      type: 'button',
      className: on ? 'chip on' : 'chip',
      textContent: label,
      'aria-pressed': on ? 'true' : 'false',
    });
    btn.addEventListener('click', () => toggle(token));
    return btn;
  }

  function paintPeople(): void {
    peopleRow.replaceChildren();
    if (!elementIds.length) {
      hint.hidden = false;
      return;
    }
    hint.hidden = true;
    for (const elementId of elementIds) {
      const chips = el('div', { className: 'chips' });
      chips.append(chip(`All ${elementLabel(elementId)}`, allToken(elementId)));
      for (const person of people.filter((p) => p.element === elementId)) {
        chips.append(chip(person.call_sign, personToken(person.id)));
      }
      peopleRow.append(
        el('div', { className: 'people-group' }, [
          el('span', { className: 'field-hint', textContent: elementLabel(elementId) }),
          chips,
        ])
      );
    }
  }

  function toggle(token: string): void {
    tokens = tokens.includes(token)
      ? tokens.filter((t) => t !== token)
      : [...tokens, token];
    paintPeople();
    paintSummary();
  }

  const element = el('div', { className: 'block' }, [
    el('span', { className: 'field-label', textContent: 'Element' }),
    elementRow,
    el('span', { className: 'field-label', textContent: 'People' }),
    hint,
    peopleRow,
    summary,
  ]);
  paintElements();
  paintPeople();

  return {
    element,
    getElement: () => elementIds[0] || '',
    getElements: () => [...elementIds],
    getTokens: () => [...tokens],
    set: (next, nextTokens) => {
      const list = Array.isArray(next) ? next : next ? [next] : [];
      elementIds = list.filter((id): id is ElementId => isElement(id));
      tokens = [...nextTokens];
      paintElements();
      paintPeople();
      paintSummary();
    },
    setPeople: (next) => {
      people = next.filter((p) => p.active);
      paintPeople();
      paintSummary();
    },
  };
}
